import type { OutreachSourceType } from "features/integrations/types";
import type { LeadsFacets, LeadsSearchParams } from "features/leads/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import type { SubscriptionType } from "common/types";

import type { QueryKey } from "@tanstack/react-query";
import type { AudienceFilters } from "../../pages/campaigns/types";
import type {
  ListLeadsSearchParams,
  SelectedNavigatorFilters,
} from "../../pages/lists/types";
import type {
  FilterOptions,
  MetricsParameters,
  ProfileSearchParams,
  SelectedFacetParam,
} from "../types";

export default function getQueryKeys(workspaceId: string) {
  const queryKeys = {
    workspaceKeys: {
      accounts: [
        ...CUSTOMER_QUERY_KEYS.workspaces.base,
        "accounts",
        workspaceId,
      ],
    },
    leadsKeys: {
      base: [{ workspaceId }, "leads"],
      listInfo: (searchParams: LeadsSearchParams<SelectedFacetParam>) => {
        return [...queryKeys.leadsKeys.base, "listInfo", searchParams];
      },
      list: (
        searchParams?: LeadsSearchParams<SelectedFacetParam>,
        pagingParams?: object,
      ) => {
        let key: QueryKey = [...queryKeys.leadsKeys.base, "list"];
        if (searchParams) {
          key = [...key, searchParams];
        }
        if (pagingParams) {
          key = [...key, pagingParams];
        }
        return key;
      },
      details: (leadId?: string) => {
        const base = [...queryKeys.leadsKeys.base, "details"];
        return leadId ? [...base, leadId] : base;
      },
      recent: (accountIds?: string[], campaignId?: string, flowId?: string) => {
        const base = [...queryKeys.leadsKeys.base, "recent"];
        if (accountIds) return [...base, accountIds];
        if (campaignId) return [...base, campaignId];
        if (flowId) return [...base, flowId];
        return base;
      },
      conversationUrn: (leadId: string) => [
        ...queryKeys.leadsKeys.base,
        "conversationUrn",
        leadId,
      ],
      facet: (
        facetName: keyof LeadsFacets,
        searchParams?: LeadsSearchParams<SelectedFacetParam>,
      ) => {
        let key: QueryKey = [...queryKeys.leadsKeys.base, "facet", facetName];
        if (searchParams) {
          key = [...key, searchParams];
        }
        return key;
      },
    },
    campaignsKeys: {
      base: [{ workspaceId }, "campaigns"],
      list: () => [...queryKeys.campaignsKeys.base, "list"],
      details: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "details",
        campaignId,
      ],
      completion: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "completion",
        campaignId,
      ],
      metrics: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "metrics",
        campaignId,
      ],
      flowMetrics: (campaignId: string, flowId: string) => [
        ...queryKeys.campaignsKeys.base,
        "flowMetrics",
        campaignId,
        flowId,
      ],
      targeting: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "targeting",
        campaignId,
      ],
      audience: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audience",
        campaignId,
      ],
      cancelledAudience: (campaignId: string) => [
        ...queryKeys.campaignsKeys.audience(campaignId),
        "cancelled",
      ],
      audienceSearch: (
        campaignId: string,
        filters: AudienceFilters,
        pagingParams?: { page: number; pageSize: number },
      ) => {
        const key: QueryKey = [
          ...queryKeys.campaignsKeys.base,
          "audienceSearch",
          campaignId,
          filters,
        ];
        if (pagingParams) {
          return [...key, pagingParams];
        }
        return key;
      },
      audienceToken: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audienceToken",
        campaignId,
      ],
      audienceStats: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audienceStats",
        campaignId,
      ],
      count: () => [...queryKeys.campaignsKeys.base, "count"],
    },
    leadListSourcesKeys: {
      base: [{ workspaceId }, "leadListSources"],
      linkedInPost: (accountId: string, postId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "post",
        accountId,
        postId,
      ],
      linkedInEvents: (accountId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "events",
        accountId,
      ],
      linkedInEvent: (accountId: string, eventId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "event",
        accountId,
        eventId,
      ],
      linkedInGroups: (accountId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "groups",
        accountId,
      ],
      linkedInGroup: (accountId: string, groupId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "group",
        accountId,
        groupId,
      ],
      crmLeads: (
        sourceId: string,
        type: OutreachSourceType,
        urlVariable?: string,
      ) => [
        ...queryKeys.leadListSourcesKeys.base,
        "crmLeads",
        sourceId,
        type,
        urlVariable,
      ],
      navigatorLeads: (
        accountId: string,
        keywords: string,
        filters: SelectedNavigatorFilters,
        pagingParams: { page: number; pageSize: number },
      ) => [
        ...queryKeys.leadListSourcesKeys.base,
        "navigatorLeads",
        accountId,
        keywords,
        filters,
        pagingParams,
      ],
      navigatorTypeahead: (accountId: string, type: string, query: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "navigatorTypeahead",
        accountId,
        type,
        query,
      ],
      profilesPreview: (
        accountId: string,
        searchParams: Partial<ProfileSearchParams>,
      ) => [
        ...queryKeys.leadListSourcesKeys.base,
        "profilesPreview",
        accountId,
        searchParams,
      ],
      searchOptions: (accountId: string, type: string, keywords?: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "searchOptions",
        accountId,
        type,
        keywords,
      ],
      searchSuggestions: (accountId: string) => [
        ...queryKeys.leadListSourcesKeys.base,
        "searchSuggestions",
        accountId,
      ],
      predefinedSearches: () => [
        ...queryKeys.leadListSourcesKeys.base,
        "predefinedSearches",
      ],
    },
    conversationsKeys: {
      base: [{ workspaceId }, "conversations"],
      list: (
        keywords?: string,
        filters?: string[],
        filteredBy?: FilterOptions,
      ) => {
        const base = [...queryKeys.conversationsKeys.base, "list"];
        return [
          ...base,
          ...(keywords ? [keywords] : []),
          ...(filters || []),
          ...(filteredBy ? [filteredBy] : []),
        ];
      },
      messages: (
        conversationUrn: string,
        accountId: string,
        lastActivityAt?: number,
      ) => {
        let key: QueryKey = [
          ...queryKeys.conversationsKeys.base,
          "messages",
          conversationUrn,
          accountId,
        ];
        if (lastActivityAt) {
          key = [...key, lastActivityAt];
        }
        return key;
      },
      details: (conversationUrn?: string, accountId?: string) => {
        let key: QueryKey = [...queryKeys.conversationsKeys.base, "details"];
        if (conversationUrn) {
          key = [...key, conversationUrn];
        }
        if (accountId) {
          key = [...key, accountId];
        }
        return key;
      },
    },
    labelsKeys: {
      list: (type: string) => [{ workspaceId }, "labels", type],
    },
    templatesKeys: {
      list: [{ workspaceId }, "templates"],
    },
    listsKeys: {
      list: [{ workspaceId }, "lists"],
      details: (listId?: string) => {
        const base = [...queryKeys.listsKeys.list, "details"];
        return listId ? [...base, listId] : base;
      },
      //the search and the paging are left off to match every page of a tab, so
      //a mutation can invalidate the tab without knowing what's listed
      leads: (
        listId: string,
        isExcluded: boolean,
        searchParams?: ListLeadsSearchParams<SelectedFacetParam>,
        pagingParams?: object,
      ): QueryKey => {
        let key: QueryKey = [
          ...queryKeys.listsKeys.list,
          listId,
          isExcluded ? "exclusions-search" : "leads-search",
        ];
        if (searchParams) {
          key = [...key, searchParams];
        }
        if (pagingParams) {
          key = [...key, pagingParams];
        }
        return key;
      },
      //one call covers both the included and the excluded counts
      leadsTotals: (listId: string): QueryKey => [
        ...queryKeys.listsKeys.list,
        listId,
        "totals",
      ],
      variables: (listId: string) => [
        ...queryKeys.listsKeys.list,
        listId,
        "custom-variables",
      ],
      //"sources" rather than "source", so the listing isn't a prefix of the
      //per-source keys below and can be invalidated on its own
      sources: (listId: string): QueryKey => [
        ...queryKeys.listsKeys.list,
        listId,
        "sources",
      ],
      sourceResultsTotal: (listId: string, sourceId: string) => [
        ...queryKeys.listsKeys.list,
        listId,
        "source",
        sourceId,
        "results-search-total",
      ],
    },
    blacklistKeys: {
      list: (type: "companies" | "accounts") => [
        { workspaceId },
        "blacklist",
        type,
      ],
    },
    companiesSearchKeys: {
      list: (keyword: string) => [{ workspaceId }, "companiesSearch", keyword],
    },
    profileSearchKeys: {
      list: (keyword: string) => [{ workspaceId }, "profilesSearch", keyword],
    },
    metricsKeys: {
      base: [{ workspaceId }, "metrics"],
      list: (
        selectedTimeframe: MetricsParameters | string,
        accountIds: string[],
        campaignId?: string,
        flowId?: string,
      ) => {
        const base = [
          ...queryKeys.metricsKeys.base,
          selectedTimeframe,
          accountIds,
        ];
        if (campaignId) return [...base, campaignId];
        if (flowId) return [...base, flowId];
        return base;
      },
    },
    membersKeys: {
      list: [{ workspaceId }, "members"],
    },
    agencyClientsKeys: {
      list: [{ workspaceId }, "agencyClients"],
    },
    invoicesKeys: {
      list: (type?: SubscriptionType) => [{ workspaceId }, "invoices", type],
    },
    webhooksKeys: {
      list: [{ workspaceId }, "webhooks"],
    },
    proxiesKeys: {
      list: [{ workspaceId }, "proxies"],
    },
    brandSettingsKey: [{ workspaceId }, "brandSettings"],
    agencySettingsKey: [{ workspaceId }, "agencySettingsKey"],
    agency: {
      base: ["agency"],
      invoices: () => [...queryKeys.agency.base, "invoices"],
    },
    integrationSettingsKey: [{ workspaceId }, "integrationSettings"],
    accountsKeys: {
      proxy: (accountId: string) => ["proxy", accountId],
      cooldown: (accountId: string) => ["cooldown", accountId],
      limits: (accountId: string) => ["limits", accountId],
      preferences: (accountId: string) => ["preferences", accountId],
      ssi: (accountId: string) => ["ssi", accountId],
      salesNavigatorContract: (accountId: string) => [
        "salesNavigatorContract",
        accountId,
      ],
      salesNavigatorContracts: (accountId: string) => [
        "salesNavigatorContracts",
        accountId,
      ],
    },
    attachmentsKeys: {
      base: [workspaceId, "attachments"],
      url: (attachmentId: string) => [
        ...queryKeys.attachmentsKeys.base,
        "attachmentUrl",
        attachmentId,
      ],
    },
    subscriptionKeys: {
      base: [workspaceId, "subscriptions"],
      renewalEstimate: (subscriptionId: string) => [
        ...queryKeys.subscriptionKeys.base,
        "estimateRenewal",
        subscriptionId,
      ],
      updateEstimate: (
        planId: string,
        quantity: number,
        isProrated: boolean,
        couponState: object,
      ) => [
        ...queryKeys.subscriptionKeys.base,
        "estimateUpdate",
        planId,
        quantity,
        isProrated,
        couponState,
      ],
      creationEstimate: (
        planId: string,
        quantity: number,
        couponId: string,
      ) => [
        ...queryKeys.subscriptionKeys.base,
        "estimateCreation",
        planId,
        quantity,
        couponId,
      ],
      rentalUpdateEstimate: (
        subscriptionId: string,
        count: number | undefined,
        itemPriceId: string | undefined,
        couponState: object,
      ) => [
        ...queryKeys.subscriptionKeys.base,
        "rentalEstimateUpdate",
        subscriptionId,
        count,
        itemPriceId,
        couponState,
      ],
    },
  };

  return queryKeys;
}
